import { ShapeSelector, Toggle } from "@mixitone/components";
import { Account, Group, defaultColors } from "@mixitone/models";
import { wwwUrl } from "@mixitone/util";
import Color from "color";
import { FormController } from "components/Forms/FormController";
import type React from "react";
import { Link } from "router";
import type { CustomComponentFieldProps, FormFieldConfig } from "./FormFieldsDefinition";

interface ColorPickerProps extends CustomComponentFieldProps<Group> {
  value: number | undefined;
  onChange: (value: number) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => {
  return (
    <div className="grid grid-cols-6 gap-2">
      {defaultColors.map((colorHex, index) => {
        const colorNumber = Color(colorHex).rgbNumber();
        const isSelected = value === colorNumber || (!value && index === 0);

        return (
          <button
            key={colorHex}
            type="button"
            className={`w-8 h-8 rounded border-2 transition-all cursor-pointer ${
              isSelected ? "border-blue-500 scale-110" : "border-gray-300 hover:border-gray-400"
            }`}
            style={{ backgroundColor: colorHex }}
            onClick={() => onChange(colorNumber)}
            title={`Color ${index + 1}`}
          />
        );
      })}
    </div>
  );
};

interface ShapePickerProps extends CustomComponentFieldProps<Group> {
  value: string | undefined;
  onChange: (value: string) => void;
}

const ShapePicker: React.FC<ShapePickerProps> = ({ value, onChange, record }) => {
  return (
    <ShapeSelector
      value={value || "circle"}
      onChange={onChange}
      color={record?.asColor?.hex() || "#2196F3"}
    />
  );
};

const GROUP_LEVELS_DOCS_URL = wwwUrl("docs/game-sessions/groups#levels-within-groups");

const GroupLevelsField: React.FC<CustomComponentFieldProps<Group>> = ({ value, onChange }) => {
  const hasLevelsFeature = Account.current?.hasFeature("group_levels");
  const checked = value ?? false;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <span className="text-sm medium:text-base">Enable levels</span>
      <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-x-2 gap-y-1">
        <Toggle
          name="graduation_enabled"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={!hasLevelsFeature}
          aria-label="Enable levels"
        />
        {hasLevelsFeature ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">Optional 1–3 graduation levels.</span>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            <Link to="/settings/account/billing" className="text-blue-500 hover:underline">
              Upgrade to Pro
            </Link>{" "}
            to enable.
          </span>
        )}
        <a
          href={GROUP_LEVELS_DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="text-xs whitespace-nowrap text-blue-500 hover:underline"
        >
          Levels guide
        </a>
      </div>
    </div>
  );
};

export class GroupFormController extends FormController<
  Group,
  {
    club_id?: string;
  }
> {
  static override initialState = {
    ...FormController.initialState,
  };

  override async buildRecord(): Promise<Group> {
    return new Group({
      ...this.props.attributes,
      name: "",
    });
  }

  override findModel(id: string) {
    return Group.query().find(id);
  }

  override async saveRecord(group: Group) {
    group.account_id = Account.current!.id;
    group.club_id ||= this.props.attributes.club_id;
    if (!Account.current?.hasFeature("group_levels")) {
      group.graduation_enabled = false;
    }
    await group.save();
  }

  fields() {
    return {
      name: {
        name: "name",
        type: "text",
        label: "Name",
        required: true,
        placeholder: "e.g. Beginner, Intermediate, Advanced",
        description: "The name of the group that players will be assigned to.",
        group: "basic",
      },
      color: {
        name: "color",
        type: "custom",
        label: "Color",
        description: "Choose a color to represent this group.",
        component: ColorPicker,
        group: "basic",
      },
      shape: {
        name: "shape",
        type: "custom",
        label: "Shape",
        description: "Choose a shape to represent this group.",
        component: ShapePicker,
        group: "basic",
      },
      graduation_enabled: {
        name: "graduation_enabled",
        type: "custom",
        label: "Enable levels",
        hideLabel: true,
        component: GroupLevelsField,
        group: "basic",
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }
}
